/**
 * screens/Approvals — Finance (read-through)
 * ──────────────────────────────────────────────────────────────────────────
 * What is waiting, and what was decided. Finance sees the whole queue; the
 * deciding happens in the approver console.
 *
 * THE ONE RULE THIS SCREEN EXISTS TO GET RIGHT:
 * "Review ›" appears on PENDING rows only. A decided row has no click handler,
 * no pointer cursor, no chevron and no hover. An affordance that does nothing
 * is worse than no affordance — someone clicks a rejected row expecting to see
 * why, gets nothing, and stops trusting the table.
 *
 * A rejection's reason is shown inline in `#96271C`, because the requester is
 * entitled to see it (invariant 5) and Finance is who they will ask.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Loading,
  Money,
  PageContainer,
  PageHeader,
  StatusPill,
  type Column,
} from '../../components';
import { neutral, status as statusTones } from '../../theme/colors';
import { sans, size as fontSize } from '../../theme/typography';
import { ellipsisStyle } from '../../theme/webStyle';
import { useAsyncData } from '../../lib/useAsyncData';
import { dateTime } from '../../lib/dates';
import { dotted } from '../../lib/format';
import { approvalState } from '../../lib/status';
import { decideBulk, fetchApprovals } from '../../services/approvals/approvals.service';
import { handleApiError } from '../../utils/errorHandler';
import { showCompleted } from '../../utils/successHandler';
import { toastWarn } from '../../components/Toast';
import type { Approval } from '../../schemas/approvals';
import { useShell } from '../../context/ShellContext';
import { APPROVALS_COPY, APPROVALS_FLOW, APPROVALS_STEPS } from './emptyState';

export function ApprovalsScreen() {
  const { dataVersion, invalidate, openModal, setView } = useShell();
  const [bulkRunning, setBulkRunning] = useState(false);

  const load = useCallback(() => fetchApprovals(), []);
  const { data, loading } = useAsyncData(load, [], {
    version: dataVersion,
    errorTitle: 'Could not load approvals',
  });

  /*
   * `data ?? []` inline would be a NEW array identity on every render, which
   * would make the memo below recompute every time — defeating the point of it.
   * Memoising the fallback keeps the identity stable while data is null.
   */
  const rows = useMemo(() => data ?? [], [data]);
  const pending = useMemo(() => rows.filter((row) => row.state === 'pending'), [rows]);

  /**
   * "Approve all". The result partitions the ids, and the SKIPPED set must be
   * reported: a bulk action that silently drops items is how a spend sits
   * unapproved for a week while everyone believes it was cleared.
   */
  const approveAll = async () => {
    setBulkRunning(true);
    try {
      const result = await decideBulk(
        pending.map((row) => row.id),
        'approve',
      );
      const parts = [
        `${result.released.length} released`,
        result.still_pending.length > 0
          ? `${result.still_pending.length} awaiting a second approval`
          : null,
        result.skipped.length > 0 ? `${result.skipped.length} skipped` : null,
      ].filter(Boolean);
      showCompleted(result, 'Approvals processed', parts.join(' · '));

      // Each skipped item gets its own reason, because "3 skipped" is not
      // actionable and the API told us exactly why for each one.
      for (const skipped of result.skipped) {
        toastWarn('One item was not approved', skipped.error);
      }
      invalidate();
    } catch (error) {
      handleApiError(error, 'Approve all failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const columns = useMemo<Column<Approval>[]>(
    () => [
      {
        key: 'request',
        header: 'Request',
        track: { fr: 1.5 },
        render: (row) => (
          <View style={styles.cellStack}>
            <Text numberOfLines={1} style={styles.label}>
              {row.label ?? row.kind}
            </Text>
            <Text numberOfLines={1} style={styles.meta}>
              {dotted(row.who, dateTime(row.created_at))}
            </Text>
          </View>
        ),
      },
      {
        key: 'amount',
        header: 'Amount',
        track: { fr: 1 },
        render: (row) =>
          row.amount_minor && row.cur ? (
            <Money value={row.amount_minor} cur={row.cur} size={fontSize.bodyLg} />
          ) : (
            <Text style={styles.meta}>—</Text>
          ),
      },
      {
        key: 'rule',
        header: 'Rule',
        track: { fr: 1.6 },
        render: (row) => (
          <Text numberOfLines={2} style={styles.rule}>
            {row.rule_summary ?? '—'}
          </Text>
        ),
      },
      {
        key: 'state',
        header: 'State',
        track: { fr: 0.9 },
        render: (row) => {
          const state = approvalState(row.state);
          return <StatusPill label={state.label} tone={state.tone} />;
        },
      },
      {
        key: 'action',
        header: '',
        track: { px: 100 },
        align: 'right',
        stackedLabel: 'Outcome',
        render: (row) =>
          row.state === 'pending' ? (
            <Text style={styles.review}>Review</Text>
          ) : row.reason ? (
            <Text numberOfLines={2} style={styles.reason}>
              {row.reason}
            </Text>
          ) : null,
      },
    ],
    [],
  );

  if (loading && !data) {
    return (
      <PageContainer>
        <Loading />
      </PageContainer>
    );
  }

  return (
    <PageContainer testID="screen-approvals">
      <PageHeader
        title="Approvals"
        subtitle="Over the threshold. Rejections carry a reason."
        right={
          pending.length > 0 ? (
            <Button
              label={`Approve all ${pending.length}`}
              size="lg"
              variant="outline"
              loading={bulkRunning}
              onPress={approveAll}
            />
          ) : undefined
        }
      />

      {rows.length === 0 ? (
        <EmptyState
          eyebrow={APPROVALS_COPY.eyebrow}
          title={APPROVALS_COPY.title}
          blurb={APPROVALS_COPY.blurb}
          nodes={APPROVALS_FLOW}
          flowNote={APPROVALS_COPY.flowNote}
          steps={APPROVALS_STEPS}
          footNote={APPROVALS_COPY.footNote}
          ctaLabel="Review approval rules"
          onCta={() => setView('settings')}
        />
      ) : (
        <Card>
          <DataTable
            columns={columns}
            rows={rows}
            keyExtractor={(row) => row.id}
            // Only pending rows are interactive — see the header comment.
            rowPressable={(row) => row.state === 'pending'}
            onRowPress={(row) => openModal({ kind: 'reject', approvalId: row.id })}
            chevron
            density="default"
          />
        </Card>
      )}
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  cellStack: { minWidth: 0, gap: 1 },
  label: { ...sans('500'), fontSize: fontSize.body, color: neutral.ink, ...ellipsisStyle },
  meta: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600, ...ellipsisStyle },
  rule: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  review: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  reason: { ...sans('400'), fontSize: fontSize.metaSm, color: statusTones.danger.text, textAlign: 'right' },
});

export default ApprovalsScreen;
